import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { supabase } from '../../../lib/supabase'
import { StatCard } from '../components/StatCard'
import { DataTable } from '../components/DataTable'
import { Button } from '../../../components/ui/Button'
import { Spinner } from '../../../components/ui/Spinner'
import { cn } from '../../../lib/utils'
import { Portal } from '../../../components/Portal'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY

async function callAdminStripe(action, extra = {}) {
  const { data: { session } } = await supabase.auth.getSession()
  const res = await fetch(`${SUPABASE_URL}/functions/v1/admin-stripe`, {
    method:  'POST',
    headers: {
      'Content-Type':  'application/json',
      'Authorization': `Bearer ${session.access_token}`,
      'apikey':        SUPABASE_KEY,
    },
    body: JSON.stringify({ action, ...extra }),
  })
  const body = await res.json()
  if (!res.ok) throw new Error(body.error ?? 'Unknown error')
  return body
}

const useMrrSummary = () =>
  useQuery({
    queryKey: ['admin', 'mrr'],
    queryFn: () => callAdminStripe('get_mrr_summary'),
  })

const useFailedPayments = () =>
  useQuery({
    queryKey: ['admin', 'failed-payments'],
    queryFn: () => callAdminStripe('get_failed_payments'),
  })

export const BillingAdminPage = () => {
  const { data: mrr, isLoading: mrrLoading }        = useMrrSummary()
  const { data: failedData, isLoading: failLoading } = useFailedPayments()

  const [refundModal, setRefundModal] = useState(null)  // { charge_id, amount, profile }
  const [refundAmount, setRefundAmount] = useState('')
  const [refundError,  setRefundError]  = useState('')

  const issueRefund = useMutation({
    mutationFn: ({ charge_id, amount }) =>
      callAdminStripe('issue_refund', {
        charge_id,
        amount: amount ? Math.round(parseFloat(amount) * 100) : undefined,
      }),
    onSuccess: () => setRefundModal(null),
    onError: (err) => setRefundError(err.message),
  })

  const failedCols = [
    { key: 'profile', label: 'Customer',
      render: (_, row) => row.profile
        ? <span>{row.profile.display_name ?? row.profile.email}</span>
        : <span className="font-mono text-xs text-text-muted">{row.customer}</span>,
    },
    { key: 'amount', label: 'Amount',
      render: (v, row) => `$${(v / 100).toFixed(2)} ${row.currency?.toUpperCase()}`,
    },
    { key: 'status', label: 'Status',
      render: (v) => (
        <span className="text-[11px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 font-semibold capitalize">{v}</span>
      ),
    },
    { key: 'created', label: 'Date',
      render: (v) => v ? new Date(v * 1000).toLocaleDateString() : '—',
    },
    { key: 'actions', label: '',
      render: (_, row) => (
        <button
          onClick={() => { setRefundModal(row); setRefundAmount((row.amount / 100).toFixed(2)); setRefundError('') }}
          className="text-xs text-amber-600 hover:text-amber-800 font-medium"
        >
          Refund
        </button>
      ),
    },
  ]

  return (
    <div className="px-8 py-8 max-w-6xl space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Billing</h1>
        <p className="text-sm text-text-muted mt-1">Revenue overview and payment management</p>
      </div>

      {/* MRR summary */}
      {mrrLoading ? (
        <div className="flex items-center gap-3"><Spinner size="sm" /><span className="text-text-muted text-sm">Loading…</span></div>
      ) : mrr ? (
        <div className="grid grid-cols-2 xl:grid-cols-3 gap-4">
          <StatCard
            title="MRR"
            value={`$${mrr.mrr?.toFixed(2)}`}
            accent="emerald"
            subtitle="Monthly recurring revenue estimate"
          />
          <StatCard title="Monthly subscribers" value={mrr.monthly?.toLocaleString()} accent="sky" />
          <StatCard title="Annual subscribers"  value={mrr.annual?.toLocaleString()}  accent="primary" />
          <StatCard title="Lifetime purchases"  value={mrr.lifetime?.toLocaleString()} accent="violet" />
          <StatCard title="Comped accounts"     value={mrr.comped?.toLocaleString()}   accent="amber" />
          <StatCard title="In trial"            value={mrr.trialing?.toLocaleString()} accent="sky" />
        </div>
      ) : (
        <p className="text-sm text-text-muted">Could not load MRR data. Stripe may not be configured.</p>
      )}

      {/* Failed payments */}
      <div>
        <h2 className="text-base font-semibold text-text-primary mb-4">Failed Payments</h2>
        <DataTable
          columns={failedCols}
          data={failedData?.failed ?? []}
          loading={failLoading}
          emptyMessage="No failed payments found."
        />
      </div>

      {/* Refund modal */}
      {refundModal && (
        <Portal>
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-surface rounded-base shadow-card w-full max-w-sm p-6 space-y-4">
            <h3 className="text-lg font-bold text-text-primary">Issue Refund</h3>
            <p className="text-sm text-text-secondary">
              Charge: <code className="font-mono text-xs">{refundModal.id}</code>
            </p>
            <div className="space-y-1">
              <label className="text-sm font-medium text-text-primary">Amount (USD)</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                max={(refundModal.amount / 100).toFixed(2)}
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                className="w-full rounded-base border border-border px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <p className="text-xs text-text-muted">
                Max: ${(refundModal.amount / 100).toFixed(2)}. Leave unchanged for full refund.
              </p>
            </div>
            {refundError && (
              <p className="text-sm text-rose-600">{refundError}</p>
            )}
            <div className="flex gap-2 justify-end">
              <Button variant="secondary" size="sm" onClick={() => setRefundModal(null)}>Cancel</Button>
              <Button
                size="sm"
                className="bg-amber-500 hover:bg-amber-600 text-white border-amber-500"
                disabled={issueRefund.isPending}
                onClick={() => issueRefund.mutate({ charge_id: refundModal.id, amount: refundAmount })}
              >
                {issueRefund.isPending ? <Spinner size="sm" /> : 'Issue Refund'}
              </Button>
            </div>
          </div>
        </div>
        </Portal>
      )}
    </div>
  )
}
